import type { DocMigration } from "../docMigration";

interface CommunityNameMigrationAttributes {
  communityName?: string;
  currentLocationName?: string;
}

const DEFAULT_IDENTIFIER_PREFIX = "identifier_";
const OLD_PARAMETER_ID = "communityName";
const NEW_PARAMETER_ID = "currentLocationName";

const COMMUNITY_NAME_TOKEN_REGEX = parameterIdRegex(OLD_PARAMETER_ID);
const REPLACEMENT_PARAMETER_TOKEN = parameterTokenNew(NEW_PARAMETER_ID);

const COMMUNITY_NAME_FORMULA_IDENTIFIER = DEFAULT_IDENTIFIER_PREFIX + OLD_PARAMETER_ID;
const CURRENT_LOCATION_NAME_FORMULA_IDENTIFIER = DEFAULT_IDENTIFIER_PREFIX + NEW_PARAMETER_ID;

const migration: DocMigration = {
  id: "20260617_rename_community_name_to_current_location_name",
  description:
    "Rename communityName to currentLocationName in condition formulas, annotations, ParameterRef objects, and home attributes",
  apply(json: any): any {
    migrateChildEntityFormulas(json);

    for (const id in json.annotation ?? {}) {
      const annotation = json.annotation[id];
      if (annotation.type === "NOTE" && typeof annotation.text === "string") {
        annotation.text = migrateText(annotation.text);
      }
    }

    migrateParameterRefs(json);
    migrateHomeAttributes(json);

    return json;
  },
};

export default migration;

function parameterIdRegex(paramId: string): RegExp {
  const regex = new RegExp(`\{\{[ ]*${paramId}[ ]*\}\}`, "g");
  return regex;
}

function parameterTokenNew(paramId: string | undefined) {
  return `{{ ${paramId} }}`;
}

function formulaNew(formula: string) {
  return { type: "FORMULA", formula };
}

function isFormula(v: any): v is { type: "FORMULA"; formula: string } {
  return (
    !!v &&
    typeof v === "object" &&
    v.hasOwnProperty("type") &&
    v.type === "FORMULA" &&
    v.hasOwnProperty("formula") &&
    typeof v.formula === "string"
  );
}

function migrateText(text: string): string {
  return text.replace(COMMUNITY_NAME_TOKEN_REGEX, REPLACEMENT_PARAMETER_TOKEN);
}

function isParameterRef(v: any): v is { type: "PARAMETER_REF"; id: string } {
  return !!v && typeof v === "object" && v.type === "PARAMETER_REF" && typeof v.id === "string";
}

function migrateParameterRefs(obj: any): void {
  if (!obj || typeof obj !== "object") {
    return;
  }

  if (Array.isArray(obj)) {
    for (const item of obj) {
      migrateParameterRefs(item);
    }
    return;
  }

  for (const key in obj) {
    const value = obj[key];
    if (isParameterRef(value) && value.id === OLD_PARAMETER_ID) {
      value.id = NEW_PARAMETER_ID;
    } else {
      migrateParameterRefs(value);
    }
  }
}

function isHomeAttributeSet(value: string | undefined): boolean {
  return value !== undefined;
}

function migrateHomeAttributes(doc: { attributes?: CommunityNameMigrationAttributes }): void {
  const attributes = doc.attributes;
  if (!attributes || typeof attributes !== "object") {
    return;
  }

  const communityName = attributes.communityName;
  const currentLocationName = attributes.currentLocationName;

  if (!isHomeAttributeSet(communityName)) {
    return;
  }

  if (isHomeAttributeSet(currentLocationName)) {
    return;
  }

  attributes.currentLocationName = communityName;
}

function migrateChildEntityFormulas(doc: any) {
  for (const docEntityName in doc) {
    for (const entityId in doc[docEntityName]) {
      const tableEntity = doc[docEntityName][entityId];
      if (!tableEntity || typeof tableEntity !== "object") {
        continue;
      }

      if (!("disabled" in tableEntity)) {
        continue;
      }

      if (!isFormula(tableEntity.disabled)) {
        continue;
      }

      const updatedFormula = formulaNew(
        tableEntity.disabled.formula.replaceAll(
          COMMUNITY_NAME_FORMULA_IDENTIFIER,
          CURRENT_LOCATION_NAME_FORMULA_IDENTIFIER,
        ),
      );

      Object.assign(tableEntity, { disabled: updatedFormula });
    }
  }
}
